import { DeleteOutlined, EditOutlined, EyeOutlined, FilePdfOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  App as AntdApp,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { CustomTable } from '../../customComponents/table/CustomTable';
import ErpService from '../../services/erp/ErpService';
import { saveBlobCrossPlatform } from '../../utils/saveBlobCrossPlatform';
import './ERP.css';

const { RangePicker } = DatePicker;

type TabKey = 'customers' | 'suppliers' | 'products' | 'customerInvoices' | 'supplierInvoices';

type LookupOption = {
  id: number;
  name: string;
  cui?: string;
  sku?: string;
  priceBuy?: number;
  priceSell?: number;
  stock?: number;
};

const initialPagination = { current: 1, pageSize: 10, total: 0 };

const ERP: React.FC = () => {
  const { message } = AntdApp.useApp();

  const [activeTab, setActiveTab] = useState<TabKey>('customers');
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const [customersPagination, setCustomersPagination] = useState(initialPagination);
  const [suppliersPagination, setSuppliersPagination] = useState(initialPagination);
  const [productsPagination, setProductsPagination] = useState(initialPagination);
  const [customerInvoicesPagination, setCustomerInvoicesPagination] = useState(initialPagination);
  const [supplierInvoicesPagination, setSupplierInvoicesPagination] = useState(initialPagination);

  const [customerFilters, setCustomerFilters] = useState<{ termen?: string; createdRange?: Dayjs[] | null }>({});
  const [supplierFilters, setSupplierFilters] = useState<{ termen?: string; createdRange?: Dayjs[] | null }>({});
  const [productFilters, setProductFilters] = useState<{
    termen?: string;
    stockFrom?: number | null;
    stockTo?: number | null;
    isBelowMinStock?: boolean;
  }>({});
  const [customerInvoiceFilters, setCustomerInvoiceFilters] = useState<{
    invoiceNumber?: string;
    customerId?: number | null;
    status?: string;
    issueRange?: Dayjs[] | null;
    dueRange?: Dayjs[] | null;
  }>({});
  const [supplierInvoiceFilters, setSupplierInvoiceFilters] = useState<{
    invoiceNumber?: string;
    supplierId?: number | null;
    status?: string;
    issueRange?: Dayjs[] | null;
    dueRange?: Dayjs[] | null;
  }>({});

  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [customerInvoiceDrawerOpen, setCustomerInvoiceDrawerOpen] = useState(false);
  const [supplierInvoiceDrawerOpen, setSupplierInvoiceDrawerOpen] = useState(false);

  const [customerForm] = Form.useForm();
  const [supplierForm] = Form.useForm();
  const [productForm] = Form.useForm();
  const [customerInvoiceForm] = Form.useForm();
  const [supplierInvoiceForm] = Form.useForm();

  const customerSearchPayload = useMemo(
    () => ({
      termen: customerFilters.termen || undefined,
      createdFrom: customerFilters.createdRange?.[0]?.startOf('day').toISOString(),
      createdTo: customerFilters.createdRange?.[1]?.endOf('day').toISOString(),
      skip: (customersPagination.current - 1) * customersPagination.pageSize,
      take: customersPagination.pageSize,
    }),
    [customerFilters, customersPagination],
  );

  const supplierSearchPayload = useMemo(
    () => ({
      termen: supplierFilters.termen || undefined,
      createdFrom: supplierFilters.createdRange?.[0]?.startOf('day').toISOString(),
      createdTo: supplierFilters.createdRange?.[1]?.endOf('day').toISOString(),
      skip: (suppliersPagination.current - 1) * suppliersPagination.pageSize,
      take: suppliersPagination.pageSize,
    }),
    [supplierFilters, suppliersPagination],
  );

  const productSearchPayload = useMemo(
    () => ({
      termen: productFilters.termen || undefined,
      stockFrom: productFilters.stockFrom ?? undefined,
      stockTo: productFilters.stockTo ?? undefined,
      isBelowMinStock: productFilters.isBelowMinStock || undefined,
      skip: (productsPagination.current - 1) * productsPagination.pageSize,
      take: productsPagination.pageSize,
    }),
    [productFilters, productsPagination],
  );

  const customerInvoiceSearchPayload = useMemo(
    () => ({
      invoiceNumber: customerInvoiceFilters.invoiceNumber || undefined,
      customerId: customerInvoiceFilters.customerId ?? undefined,
      status: customerInvoiceFilters.status || undefined,
      issueDateFrom: customerInvoiceFilters.issueRange?.[0]?.startOf('day').toISOString(),
      issueDateTo: customerInvoiceFilters.issueRange?.[1]?.endOf('day').toISOString(),
      dueDateFrom: customerInvoiceFilters.dueRange?.[0]?.startOf('day').toISOString(),
      dueDateTo: customerInvoiceFilters.dueRange?.[1]?.endOf('day').toISOString(),
      skip: (customerInvoicesPagination.current - 1) * customerInvoicesPagination.pageSize,
      take: customerInvoicesPagination.pageSize,
    }),
    [customerInvoiceFilters, customerInvoicesPagination],
  );

  const supplierInvoiceSearchPayload = useMemo(
    () => ({
      invoiceNumber: supplierInvoiceFilters.invoiceNumber || undefined,
      supplierId: supplierInvoiceFilters.supplierId ?? undefined,
      status: supplierInvoiceFilters.status || undefined,
      issueDateFrom: supplierInvoiceFilters.issueRange?.[0]?.startOf('day').toISOString(),
      issueDateTo: supplierInvoiceFilters.issueRange?.[1]?.endOf('day').toISOString(),
      dueDateFrom: supplierInvoiceFilters.dueRange?.[0]?.startOf('day').toISOString(),
      dueDateTo: supplierInvoiceFilters.dueRange?.[1]?.endOf('day').toISOString(),
      skip: (supplierInvoicesPagination.current - 1) * supplierInvoicesPagination.pageSize,
      take: supplierInvoicesPagination.pageSize,
    }),
    [supplierInvoiceFilters, supplierInvoicesPagination],
  );

  const customersQuery = useQuery({
    queryKey: ['erp-customers', customerSearchPayload, refreshKey],
    queryFn: () => ErpService.searchCustomers(customerSearchPayload),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const suppliersQuery = useQuery({
    queryKey: ['erp-suppliers', supplierSearchPayload, refreshKey],
    queryFn: () => ErpService.searchSuppliers(supplierSearchPayload),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const productsQuery = useQuery({
    queryKey: ['erp-products', productSearchPayload, refreshKey],
    queryFn: () => ErpService.searchProducts(productSearchPayload),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const customerInvoicesQuery = useQuery({
    queryKey: ['erp-customer-invoices', customerInvoiceSearchPayload, refreshKey],
    queryFn: () => ErpService.searchCustomerInvoices(customerInvoiceSearchPayload),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const supplierInvoicesQuery = useQuery({
    queryKey: ['erp-supplier-invoices', supplierInvoiceSearchPayload, refreshKey],
    queryFn: () => ErpService.searchSupplierInvoices(supplierInvoiceSearchPayload),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const customerLookupQuery = useQuery({
    queryKey: ['erp-customer-lookup', refreshKey],
    queryFn: () => ErpService.searchCustomers({ take: 1000, skip: 0 }),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const supplierLookupQuery = useQuery({
    queryKey: ['erp-supplier-lookup', refreshKey],
    queryFn: () => ErpService.searchSuppliers({ take: 1000, skip: 0 }),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const productLookupQuery = useQuery({
    queryKey: ['erp-product-lookup', refreshKey],
    queryFn: () => ErpService.searchProducts({ take: 1000, skip: 0 }),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setCustomersPagination((prev) => ({ ...prev, total: customersQuery.data?.totalItems || 0 }));
  }, [customersQuery.data?.totalItems]);

  useEffect(() => {
    setSuppliersPagination((prev) => ({ ...prev, total: suppliersQuery.data?.totalItems || 0 }));
  }, [suppliersQuery.data?.totalItems]);

  useEffect(() => {
    setProductsPagination((prev) => ({ ...prev, total: productsQuery.data?.totalItems || 0 }));
  }, [productsQuery.data?.totalItems]);

  useEffect(() => {
    setCustomerInvoicesPagination((prev) => ({ ...prev, total: customerInvoicesQuery.data?.totalItems || 0 }));
  }, [customerInvoicesQuery.data?.totalItems]);

  useEffect(() => {
    setSupplierInvoicesPagination((prev) => ({ ...prev, total: supplierInvoicesQuery.data?.totalItems || 0 }));
  }, [supplierInvoicesQuery.data?.totalItems]);

  const customerOptions: LookupOption[] = customerLookupQuery.data?.response ?? [];
  const supplierOptions: LookupOption[] = supplierLookupQuery.data?.response ?? [];
  const productOptions: LookupOption[] = productLookupQuery.data?.response ?? [];

  const selectedCustomerInvoiceLines = Form.useWatch('lines', customerInvoiceForm) || [];
  const selectedSupplierInvoiceLines = Form.useWatch('lines', supplierInvoiceForm) || [];

  const customerTotals = computeInvoiceTotals(selectedCustomerInvoiceLines);
  const supplierTotals = computeInvoiceTotals(selectedSupplierInvoiceLines);

  const tableLoading =
    customersQuery.isFetching ||
    suppliersQuery.isFetching ||
    productsQuery.isFetching ||
    customerInvoicesQuery.isFetching ||
    supplierInvoicesQuery.isFetching;

  const handleApiError = (errors?: string[] | null, fallback?: string) => {
    if (errors && errors.length > 0) {
      message.error(errors[0]);
      return;
    }

    if (fallback) {
      message.error(fallback);
    }
  };

  const triggerRefresh = () => setRefreshKey((prev) => prev + 1);

  const handleCustomerSubmit = async () => {
    const values = await customerForm.validateFields();
    const response = await ErpService.upsertCustomer(values);
    if (response?.errors?.length) {
      handleApiError(response.errors);
      return;
    }

    message.success(values.id ? 'Client actualizat.' : 'Client adaugat.');
    setCustomerModalOpen(false);
    customerForm.resetFields();
    triggerRefresh();
  };

  const handleSupplierSubmit = async () => {
    const values = await supplierForm.validateFields();
    const response = await ErpService.upsertSupplier(values);
    if (response?.errors?.length) {
      handleApiError(response.errors);
      return;
    }

    message.success(values.id ? 'Furnizor actualizat.' : 'Furnizor adaugat.');
    setSupplierModalOpen(false);
    supplierForm.resetFields();
    triggerRefresh();
  };

  const handleProductSubmit = async () => {
    const values = await productForm.validateFields();
    const response = await ErpService.upsertProduct(values);
    if (response?.errors?.length) {
      handleApiError(response.errors);
      return;
    }

    message.success(values.id ? 'Produs actualizat.' : 'Produs adaugat.');
    setProductModalOpen(false);
    productForm.resetFields();
    triggerRefresh();
  };

  const handleCustomerInvoiceSubmit = async () => {
    const values = await customerInvoiceForm.validateFields();
    const payload = {
      ...values,
      issueDate: values.issueDate.toISOString(),
      dueDate: values.dueDate.toISOString(),
      lines: (values.lines || []).map((line: any) => ({
        ...line,
        unitPrice: Number(line.unitPrice || 0),
        vatPercentage: Number(line.vatPercentage || 0),
        quantity: Number(line.quantity || 0),
      })),
    };

    const response = await ErpService.upsertCustomerInvoice(payload);
    if (response?.errors?.length) {
      handleApiError(response.errors);
      return;
    }
    if (!response?.response) {
      handleApiError(null, 'Factura client nu a fost salvata.');
      return;
    }

    message.success(values.id ? 'Factura client actualizata.' : 'Factura client adaugata.');
    setCustomerInvoiceDrawerOpen(false);
    customerInvoiceForm.resetFields();
    triggerRefresh();
  };

  const handleSupplierInvoiceSubmit = async () => {
    const values = await supplierInvoiceForm.validateFields();
    const payload = {
      ...values,
      issueDate: values.issueDate.toISOString(),
      dueDate: values.dueDate.toISOString(),
      lines: (values.lines || []).map((line: any) => ({
        ...line,
        unitPrice: Number(line.unitPrice || 0),
        vatPercentage: Number(line.vatPercentage || 0),
        quantity: Number(line.quantity || 0),
      })),
    };

    const response = await ErpService.upsertSupplierInvoice(payload);
    if (response?.errors?.length) {
      handleApiError(response.errors);
      return;
    }
    if (!response?.response) {
      handleApiError(null, 'Factura furnizor nu a fost salvata.');
      return;
    }

    message.success(values.id ? 'Factura furnizor actualizata.' : 'Factura furnizor adaugata.');
    setSupplierInvoiceDrawerOpen(false);
    supplierInvoiceForm.resetFields();
    triggerRefresh();
  };

  const openCustomerModal = (record?: any) => {
    customerForm.setFieldsValue(record ?? { id: 0 });
    setCustomerModalOpen(true);
  };

  const openSupplierModal = (record?: any) => {
    supplierForm.setFieldsValue(record ?? { id: 0 });
    setSupplierModalOpen(true);
  };

  const openProductModal = (record?: any) => {
    productForm.setFieldsValue(record ?? { id: 0, priceBuy: 0, priceSell: 0, stock: 0, minStock: 0 });
    setProductModalOpen(true);
  };

  const openCustomerInvoiceDrawer = (record?: any) => {
    customerInvoiceForm.setFieldsValue(
      record
        ? {
            ...record,
            issueDate: dayjs(record.issueDate),
            dueDate: dayjs(record.dueDate),
            lines: (record.lines || []).map((line: any) => ({
              id: line.id,
              productId: line.productId,
              quantity: line.quantity,
              unitPrice: line.unitPrice,
              vatPercentage: line.vatPercentage,
            })),
          }
        : {
            id: 0,
            status: 'Draft',
            issueDate: dayjs(),
            dueDate: dayjs(),
            lines: [{ productId: undefined, quantity: 1, unitPrice: 0, vatPercentage: 19 }],
          },
    );
    setCustomerInvoiceDrawerOpen(true);
  };

  const openSupplierInvoiceDrawer = (record?: any) => {
    supplierInvoiceForm.setFieldsValue(
      record
        ? {
            ...record,
            issueDate: dayjs(record.issueDate),
            dueDate: dayjs(record.dueDate),
            lines: (record.lines || []).map((line: any) => ({
              id: line.id,
              productId: line.productId,
              quantity: line.quantity,
              unitPrice: line.unitPrice,
              vatPercentage: line.vatPercentage,
            })),
          }
        : {
            id: 0,
            status: 'Draft',
            issueDate: dayjs(),
            dueDate: dayjs(),
            lines: [{ productId: undefined, quantity: 1, unitPrice: 0, vatPercentage: 19 }],
          },
    );
    setSupplierInvoiceDrawerOpen(true);
  };

  const handleDelete = async (type: TabKey, id: number) => {
    let response: any;

    if (type === 'customers') response = await ErpService.deleteCustomer(id);
    if (type === 'suppliers') response = await ErpService.deleteSupplier(id);
    if (type === 'products') response = await ErpService.deleteProduct(id);
    if (type === 'customerInvoices') response = await ErpService.deleteCustomerInvoice(id);
    if (type === 'supplierInvoices') response = await ErpService.deleteSupplierInvoice(id);

    if (response?.errors?.length) {
      handleApiError(response.errors);
      return;
    }

    message.success('Inregistrarea a fost stearsa.');
    triggerRefresh();
  };

  const handlePdfDownload = async (type: 'customer' | 'supplier', id: number) => {
    const response =
      type === 'customer'
        ? await ErpService.downloadCustomerInvoicePdf(id)
        : await ErpService.downloadSupplierInvoicePdf(id);

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = type === 'customer' ? 'factura-client.pdf' : 'factura-furnizor.pdf';

    if (contentDisposition) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
      if (matches?.[1]) {
        filename = matches[1].replace(/['"]/g, '');
      }
    }

    const blob = await response.blob();
    saveBlobCrossPlatform(blob, filename);
  };

  const setCustomerInvoiceLineDefaults = (index: number, productId: number) => {
    const product = productOptions.find((item) => item.id === productId);
    if (!product) return;

    const lines = [...(customerInvoiceForm.getFieldValue('lines') || [])];
    lines[index] = {
      ...lines[index],
      productId,
      unitPrice: lines[index]?.unitPrice ?? product.priceSell ?? 0,
      vatPercentage: lines[index]?.vatPercentage ?? 19,
    };
    customerInvoiceForm.setFieldsValue({ lines });
  };

  const setSupplierInvoiceLineDefaults = (index: number, productId: number) => {
    const product = productOptions.find((item) => item.id === productId);
    if (!product) return;

    const lines = [...(supplierInvoiceForm.getFieldValue('lines') || [])];
    lines[index] = {
      ...lines[index],
      productId,
      unitPrice: lines[index]?.unitPrice ?? product.priceBuy ?? 0,
      vatPercentage: lines[index]?.vatPercentage ?? 19,
    };
    supplierInvoiceForm.setFieldsValue({ lines });
  };

  const customerColumns = [
    { title: 'Denumire', dataIndex: 'name', key: 'name' },
    { title: 'CUI', dataIndex: 'cui', key: 'cui' },
    { title: 'Reg. Com.', dataIndex: 'regCom', key: 'regCom' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Telefon', dataIndex: 'phone', key: 'phone' },
    { title: 'Adresa', dataIndex: 'address', key: 'address', width: 320 },
    {
      title: 'Actiuni',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openCustomerModal(record)} />
          <Popconfirm title='Stergi clientul?' onConfirm={() => handleDelete('customers', record.id)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const supplierColumns = [
    { title: 'Denumire', dataIndex: 'name', key: 'name' },
    { title: 'CUI', dataIndex: 'cui', key: 'cui' },
    { title: 'Reg. Com.', dataIndex: 'regCom', key: 'regCom' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Telefon', dataIndex: 'phone', key: 'phone' },
    { title: 'Adresa', dataIndex: 'address', key: 'address', width: 320 },
    {
      title: 'Actiuni',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openSupplierModal(record)} />
          <Popconfirm title='Stergi furnizorul?' onConfirm={() => handleDelete('suppliers', record.id)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const productColumns = [
    { title: 'SKU', dataIndex: 'sku', key: 'sku' },
    { title: 'Denumire', dataIndex: 'name', key: 'name', width: 240 },
    { title: 'Pret achizitie', dataIndex: 'priceBuy', key: 'priceBuy' },
    { title: 'Pret vanzare', dataIndex: 'priceSell', key: 'priceSell' },
    { title: 'Stoc', dataIndex: 'stock', key: 'stock' },
    { title: 'Stoc minim', dataIndex: 'minStock', key: 'minStock' },
    {
      title: 'Status stoc',
      key: 'stockStatus',
      render: (_: any, record: any) =>
        record.stock <= record.minStock ? <Tag color='red'>Sub minim</Tag> : <Tag color='green'>OK</Tag>,
    },
    {
      title: 'Actiuni',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openProductModal(record)} />
          <Popconfirm title='Stergi produsul?' onConfirm={() => handleDelete('products', record.id)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const customerInvoiceColumns = [
    { title: 'Numar', dataIndex: 'invoiceNumber', key: 'invoiceNumber' },
    { title: 'Client', dataIndex: 'customerName', key: 'customerName', width: 240 },
    {
      title: 'Data emitere',
      dataIndex: 'issueDate',
      key: 'issueDate',
      render: (value: string) => dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: 'Scadenta',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (value: string) => dayjs(value).format('DD.MM.YYYY'),
    },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Total net', dataIndex: 'totalNet', key: 'totalNet' },
    { title: 'Total TVA', dataIndex: 'totalVat', key: 'totalVat' },
    { title: 'Total brut', dataIndex: 'totalGross', key: 'totalGross' },
    {
      title: 'Actiuni',
      key: 'actions',
      fixed: 'right',
      width: 220,
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => openCustomerInvoiceDrawer(record)} />
          <Button icon={<FilePdfOutlined />} onClick={() => handlePdfDownload('customer', record.id)} />
          <Popconfirm title='Stergi factura?' onConfirm={() => handleDelete('customerInvoices', record.id)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const supplierInvoiceColumns = [
    { title: 'Numar', dataIndex: 'invoiceNumber', key: 'invoiceNumber' },
    { title: 'Furnizor', dataIndex: 'supplierName', key: 'supplierName', width: 240 },
    {
      title: 'Data emitere',
      dataIndex: 'issueDate',
      key: 'issueDate',
      render: (value: string) => dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: 'Scadenta',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (value: string) => dayjs(value).format('DD.MM.YYYY'),
    },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Total net', dataIndex: 'totalNet', key: 'totalNet' },
    { title: 'Total TVA', dataIndex: 'totalVat', key: 'totalVat' },
    { title: 'Total brut', dataIndex: 'totalGross', key: 'totalGross' },
    {
      title: 'Actiuni',
      key: 'actions',
      fixed: 'right',
      width: 220,
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => openSupplierInvoiceDrawer(record)} />
          <Button icon={<FilePdfOutlined />} onClick={() => handlePdfDownload('supplier', record.id)} />
          <Popconfirm title='Stergi factura?' onConfirm={() => handleDelete('supplierInvoices', record.id)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const anyLoading =
    tableLoading || customerLookupQuery.isFetching || supplierLookupQuery.isFetching || productLookupQuery.isFetching;

  if (anyLoading && !customersQuery.data && !suppliersQuery.data && !productsQuery.data) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <div className='erp-page'>
      <h1>ERP</h1>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as TabKey)}
        items={[
          {
            key: 'customers',
            label: 'Clienti',
            children: (
              <>
                <div className='erp-toolbar' style={{ marginBottom: '20px' }}>
                  <div className='erp-toolbar-left'>
                    <Input
                      className='erp-inline-field-wide'
                      placeholder='Cauta dupa denumire, CUI, email, telefon'
                      value={customerFilters.termen}
                      onChange={(e) => {
                        setCustomersPagination((prev) => ({ ...prev, current: 1 }));
                        setCustomerFilters((prev) => ({ ...prev, termen: e.target.value }));
                      }}
                    />
                    <RangePicker
                      value={customerFilters.createdRange as any}
                      onChange={(value) => {
                        setCustomersPagination((prev) => ({ ...prev, current: 1 }));
                        setCustomerFilters((prev) => ({ ...prev, createdRange: value as Dayjs[] | null }));
                      }}
                    />
                  </div>
                  <Button type='primary' icon={<PlusOutlined />} onClick={() => openCustomerModal()}>
                    Adauga client
                  </Button>
                </div>
                <CustomTable
                  rowKey='id'
                  columns={customerColumns}
                  initialDataSource={customersQuery.data?.response ?? []}
                  pagination={buildPagination(customersPagination, setCustomersPagination)}
                />
              </>
            ),
          },
          {
            key: 'suppliers',
            label: 'Furnizori',
            children: (
              <>
                <div className='erp-toolbar' style={{ marginBottom: '20px' }}>
                  <div className='erp-toolbar-left'>
                    <Input
                      className='erp-inline-field-wide'
                      placeholder='Cauta dupa denumire, CUI, email, telefon'
                      value={supplierFilters.termen}
                      onChange={(e) => {
                        setSuppliersPagination((prev) => ({ ...prev, current: 1 }));
                        setSupplierFilters((prev) => ({ ...prev, termen: e.target.value }));
                      }}
                    />
                    <RangePicker
                      value={supplierFilters.createdRange as any}
                      onChange={(value) => {
                        setSuppliersPagination((prev) => ({ ...prev, current: 1 }));
                        setSupplierFilters((prev) => ({ ...prev, createdRange: value as Dayjs[] | null }));
                      }}
                    />
                  </div>
                  <Button type='primary' icon={<PlusOutlined />} onClick={() => openSupplierModal()}>
                    Adauga furnizor
                  </Button>
                </div>
                <CustomTable
                  rowKey='id'
                  columns={supplierColumns}
                  initialDataSource={suppliersQuery.data?.response ?? []}
                  pagination={buildPagination(suppliersPagination, setSuppliersPagination)}
                />
              </>
            ),
          },
          {
            key: 'products',
            label: 'Produse',
            children: (
              <>
                <div className='erp-toolbar' style={{ marginBottom: '20px' }}>
                  <div className='erp-toolbar-left'>
                    <Input
                      className='erp-inline-field-wide'
                      placeholder='Cauta dupa SKU sau denumire'
                      value={productFilters.termen}
                      onChange={(e) => {
                        setProductsPagination((prev) => ({ ...prev, current: 1 }));
                        setProductFilters((prev) => ({ ...prev, termen: e.target.value }));
                      }}
                    />
                    <InputNumber
                      className='erp-inline-field'
                      placeholder='Stoc de la'
                      value={productFilters.stockFrom ?? undefined}
                      onChange={(value) => {
                        setProductsPagination((prev) => ({ ...prev, current: 1 }));
                        setProductFilters((prev) => ({ ...prev, stockFrom: value }));
                      }}
                    />
                    <InputNumber
                      className='erp-inline-field'
                      placeholder='Stoc pana la'
                      value={productFilters.stockTo ?? undefined}
                      onChange={(value) => {
                        setProductsPagination((prev) => ({ ...prev, current: 1 }));
                        setProductFilters((prev) => ({ ...prev, stockTo: value }));
                      }}
                    />
                    <Select
                      className='erp-inline-field'
                      allowClear
                      placeholder='Filtru stoc'
                      value={
                        productFilters.isBelowMinStock === undefined
                          ? undefined
                          : productFilters.isBelowMinStock
                            ? 'sub'
                            : 'toate'
                      }
                      onChange={(value) => {
                        setProductsPagination((prev) => ({ ...prev, current: 1 }));
                        setProductFilters((prev) => ({
                          ...prev,
                          isBelowMinStock: value === 'sub' ? true : undefined,
                        }));
                      }}
                      options={[
                        { value: 'sub', label: 'Sub stoc minim' },
                        { value: 'toate', label: 'Toate' },
                      ]}
                    />
                  </div>
                  <Button type='primary' icon={<PlusOutlined />} onClick={() => openProductModal()}>
                    Adauga produs
                  </Button>
                </div>
                <CustomTable
                  rowKey='id'
                  columns={productColumns}
                  initialDataSource={productsQuery.data?.response ?? []}
                  pagination={buildPagination(productsPagination, setProductsPagination)}
                />
              </>
            ),
          },
          {
            key: 'customerInvoices',
            label: 'Facturi clienti',
            children: (
              <>
                <div className='erp-toolbar' style={{ marginBottom: '20px' }}>
                  <div className='erp-toolbar-left'>
                    <Input
                      className='erp-inline-field'
                      placeholder='Numar factura'
                      value={customerInvoiceFilters.invoiceNumber}
                      onChange={(e) => {
                        setCustomerInvoicesPagination((prev) => ({ ...prev, current: 1 }));
                        setCustomerInvoiceFilters((prev) => ({ ...prev, invoiceNumber: e.target.value }));
                      }}
                    />
                    <Select
                      className='erp-inline-field-wide'
                      allowClear
                      showSearch
                      placeholder='Client'
                      value={customerInvoiceFilters.customerId ?? undefined}
                      optionFilterProp='label'
                      onChange={(value) => {
                        setCustomerInvoicesPagination((prev) => ({ ...prev, current: 1 }));
                        setCustomerInvoiceFilters((prev) => ({ ...prev, customerId: value ?? null }));
                      }}
                      options={customerOptions.map((item) => ({
                        value: item.id,
                        label: `${item.name} (${item.cui || '-'})`,
                      }))}
                    />
                    <Select
                      className='erp-inline-field'
                      allowClear
                      placeholder='Status'
                      value={customerInvoiceFilters.status}
                      onChange={(value) => {
                        setCustomerInvoicesPagination((prev) => ({ ...prev, current: 1 }));
                        setCustomerInvoiceFilters((prev) => ({ ...prev, status: value }));
                      }}
                      options={[
                        { value: 'Draft', label: 'Draft' },
                        { value: 'Emisa', label: 'Emisa' },
                        { value: 'Platita', label: 'Platita' },
                      ]}
                    />
                    <RangePicker
                      value={customerInvoiceFilters.issueRange as any}
                      onChange={(value) => {
                        setCustomerInvoicesPagination((prev) => ({ ...prev, current: 1 }));
                        setCustomerInvoiceFilters((prev) => ({ ...prev, issueRange: value as Dayjs[] | null }));
                      }}
                    />
                    <RangePicker
                      value={customerInvoiceFilters.dueRange as any}
                      onChange={(value) => {
                        setCustomerInvoicesPagination((prev) => ({ ...prev, current: 1 }));
                        setCustomerInvoiceFilters((prev) => ({ ...prev, dueRange: value as Dayjs[] | null }));
                      }}
                    />
                  </div>
                  <Button type='primary' icon={<PlusOutlined />} onClick={() => openCustomerInvoiceDrawer()}>
                    Adauga factura client
                  </Button>
                </div>
                <CustomTable
                  rowKey='id'
                  columns={customerInvoiceColumns}
                  initialDataSource={customerInvoicesQuery.data?.response ?? []}
                  pagination={buildPagination(customerInvoicesPagination, setCustomerInvoicesPagination)}
                />
              </>
            ),
          },
          {
            key: 'supplierInvoices',
            label: 'Facturi furnizori',
            children: (
              <>
                <div className='erp-toolbar' style={{ marginBottom: '20px' }}>
                  <div className='erp-toolbar-left'>
                    <Input
                      className='erp-inline-field'
                      placeholder='Numar factura'
                      value={supplierInvoiceFilters.invoiceNumber}
                      onChange={(e) => {
                        setSupplierInvoicesPagination((prev) => ({ ...prev, current: 1 }));
                        setSupplierInvoiceFilters((prev) => ({ ...prev, invoiceNumber: e.target.value }));
                      }}
                    />
                    <Select
                      className='erp-inline-field-wide'
                      allowClear
                      showSearch
                      placeholder='Furnizor'
                      value={supplierInvoiceFilters.supplierId ?? undefined}
                      optionFilterProp='label'
                      onChange={(value) => {
                        setSupplierInvoicesPagination((prev) => ({ ...prev, current: 1 }));
                        setSupplierInvoiceFilters((prev) => ({ ...prev, supplierId: value ?? null }));
                      }}
                      options={supplierOptions.map((item) => ({
                        value: item.id,
                        label: `${item.name} (${item.cui || '-'})`,
                      }))}
                    />
                    <RangePicker
                      value={supplierInvoiceFilters.issueRange as any}
                      onChange={(value) => {
                        setSupplierInvoicesPagination((prev) => ({ ...prev, current: 1 }));
                        setSupplierInvoiceFilters((prev) => ({ ...prev, issueRange: value as Dayjs[] | null }));
                      }}
                    />
                    <RangePicker
                      value={supplierInvoiceFilters.dueRange as any}
                      onChange={(value) => {
                        setSupplierInvoicesPagination((prev) => ({ ...prev, current: 1 }));
                        setSupplierInvoiceFilters((prev) => ({ ...prev, dueRange: value as Dayjs[] | null }));
                      }}
                    />
                    <Select
                      className='erp-inline-field'
                      allowClear
                      placeholder='Status'
                      value={supplierInvoiceFilters.status}
                      onChange={(value) => {
                        setSupplierInvoicesPagination((prev) => ({ ...prev, current: 1 }));
                        setSupplierInvoiceFilters((prev) => ({ ...prev, status: value }));
                      }}
                      options={[
                        { value: 'Draft', label: 'Draft' },
                        { value: 'Emisa', label: 'Emisa' },
                        { value: 'Platita', label: 'Platita' },
                      ]}
                    />
                  </div>
                  <Button type='primary' icon={<PlusOutlined />} onClick={() => openSupplierInvoiceDrawer()}>
                    Adauga factura furnizor
                  </Button>
                </div>
                <CustomTable
                  rowKey='id'
                  columns={supplierInvoiceColumns}
                  initialDataSource={supplierInvoicesQuery.data?.response ?? []}
                  pagination={buildPagination(supplierInvoicesPagination, setSupplierInvoicesPagination)}
                />
              </>
            ),
          },
        ]}
      />

      <Modal
        title='Client'
        open={customerModalOpen}
        onCancel={() => setCustomerModalOpen(false)}
        onOk={handleCustomerSubmit}
      >
        <Form form={customerForm} layout='vertical'>
          <Form.Item name='id' hidden>
            <Input />
          </Form.Item>
          <Form.Item
            label='Denumire'
            name='name'
            rules={[{ required: true, message: 'Introdu denumirea clientului.' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label='CUI' name='cui' rules={[{ required: true, message: 'Introdu CUI-ul clientului.' }]}>
            <Input />
          </Form.Item>
          <Form.Item label='Nr. Reg. Com.' name='regCom'>
            <Input />
          </Form.Item>
          <Form.Item label='Email' name='email'>
            <Input />
          </Form.Item>
          <Form.Item label='Telefon' name='phone'>
            <Input />
          </Form.Item>
          <Form.Item label='Adresa' name='address' rules={[{ required: true, message: 'Introdu adresa clientului.' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title='Furnizor'
        open={supplierModalOpen}
        onCancel={() => setSupplierModalOpen(false)}
        onOk={handleSupplierSubmit}
      >
        <Form form={supplierForm} layout='vertical'>
          <Form.Item name='id' hidden>
            <Input />
          </Form.Item>
          <Form.Item
            label='Denumire'
            name='name'
            rules={[{ required: true, message: 'Introdu denumirea furnizorului.' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label='CUI' name='cui' rules={[{ required: true, message: 'Introdu CUI-ul furnizorului.' }]}>
            <Input />
          </Form.Item>
          <Form.Item label='Nr. Reg. Com.' name='regCom'>
            <Input />
          </Form.Item>
          <Form.Item label='Email' name='email'>
            <Input />
          </Form.Item>
          <Form.Item label='Telefon' name='phone'>
            <Input />
          </Form.Item>
          <Form.Item
            label='Adresa'
            name='address'
            rules={[{ required: true, message: 'Introdu adresa furnizorului.' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title='Produs'
        open={productModalOpen}
        onCancel={() => setProductModalOpen(false)}
        onOk={handleProductSubmit}
      >
        <Form form={productForm} layout='vertical'>
          <Form.Item name='id' hidden>
            <Input />
          </Form.Item>
          <Form.Item label='SKU' name='sku' rules={[{ required: true, message: 'Introdu SKU-ul produsului.' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label='Denumire'
            name='name'
            rules={[{ required: true, message: 'Introdu denumirea produsului.' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label='Pret achizitie'
            name='priceBuy'
            rules={[{ required: true, message: 'Introdu pretul de achizitie.' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} precision={4} />
          </Form.Item>
          <Form.Item
            label='Pret vanzare'
            name='priceSell'
            rules={[{ required: true, message: 'Introdu pretul de vanzare.' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} precision={4} />
          </Form.Item>
          <Form.Item label='Stoc' name='stock' rules={[{ required: true, message: 'Introdu stocul.' }]}>
            <InputNumber style={{ width: '100%' }} min={0} precision={0} />
          </Form.Item>
          <Form.Item label='Stoc minim' name='minStock' rules={[{ required: true, message: 'Introdu stocul minim.' }]}>
            <InputNumber style={{ width: '100%' }} min={0} precision={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title='Factura client'
        width={960}
        open={customerInvoiceDrawerOpen}
        onClose={() => setCustomerInvoiceDrawerOpen(false)}
        extra={
          <Button type='primary' onClick={handleCustomerInvoiceSubmit}>
            Salveaza
          </Button>
        }
      >
        <Form form={customerInvoiceForm} layout='vertical'>
          <Form.Item name='id' hidden>
            <Input />
          </Form.Item>
          <Space style={{ width: '100%' }} size={16} wrap>
            <Form.Item
              label='Numar factura'
              name='invoiceNumber'
              rules={[{ required: true, message: 'Introdu numarul facturii.' }]}
            >
              <Input className='erp-inline-field' />
            </Form.Item>
            <Form.Item label='Client' name='customerId' rules={[{ required: true, message: 'Selecteaza clientul.' }]}>
              <Select
                className='erp-inline-field-wide'
                showSearch
                optionFilterProp='label'
                options={customerOptions.map((item) => ({
                  value: item.id,
                  label: `${item.name} (${item.cui || '-'})`,
                }))}
              />
            </Form.Item>
            <Form.Item
              label='Data emitere'
              name='issueDate'
              rules={[{ required: true, message: 'Selecteaza data emiterii.' }]}
            >
              <DatePicker format='DD.MM.YYYY' />
            </Form.Item>
            <Form.Item
              label='Data scadenta'
              name='dueDate'
              rules={[{ required: true, message: 'Selecteaza scadenta.' }]}
            >
              <DatePicker format='DD.MM.YYYY' />
            </Form.Item>
            <Form.Item label='Status' name='status' rules={[{ required: true, message: 'Selecteaza statusul.' }]}>
              <Select
                options={[
                  { value: 'Draft', label: 'Draft' },
                  { value: 'Emisa', label: 'Emisa' },
                  { value: 'Platita', label: 'Platita' },
                ]}
              />
            </Form.Item>
          </Space>

          <Form.List name='lines'>
            {(fields, { add, remove }) => (
              <>
                <div className='erp-lines-header'>
                  <h3>Linii factura</h3>
                  <Button onClick={() => add({ quantity: 1, unitPrice: 0, vatPercentage: 19 })}>Adauga linie</Button>
                </div>
                {fields.map((field, index) => (
                  <Space key={field.key} align='start' style={{ display: 'flex', marginBottom: 8 }} wrap>
                    <Form.Item
                      {...field}
                      label='Produs'
                      name={[field.name, 'productId']}
                      rules={[{ required: true, message: 'Selecteaza produsul.' }]}
                    >
                      <Select
                        className='erp-inline-field-wide'
                        showSearch
                        optionFilterProp='label'
                        options={productOptions.map((item) => ({
                          value: item.id,
                          label: `${item.sku || '-'} - ${item.name} (stoc ${item.stock ?? 0})`,
                        }))}
                        onChange={(value) => setCustomerInvoiceLineDefaults(index, value)}
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      label='Cantitate'
                      name={[field.name, 'quantity']}
                      rules={[{ required: true, message: 'Cantitate.' }]}
                    >
                      <InputNumber min={1} precision={0} />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      label='Pret unitar'
                      name={[field.name, 'unitPrice']}
                      rules={[{ required: true, message: 'Pret unitar.' }]}
                    >
                      <InputNumber min={0} precision={4} />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      label='TVA %'
                      name={[field.name, 'vatPercentage']}
                      rules={[{ required: true, message: 'TVA.' }]}
                    >
                      <InputNumber min={0} precision={2} />
                    </Form.Item>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                      style={{ marginTop: 30 }}
                    />
                  </Space>
                ))}
              </>
            )}
          </Form.List>

          <div className='erp-summary'>
            <div className='erp-summary-card'>
              <strong>Total net:</strong>
              <div>{customerTotals.totalNet.toFixed(4)}</div>
            </div>
            <div className='erp-summary-card'>
              <strong>Total TVA:</strong>
              <div>{customerTotals.totalVat.toFixed(4)}</div>
            </div>
            <div className='erp-summary-card'>
              <strong>Total brut:</strong>
              <div>{customerTotals.totalGross.toFixed(4)}</div>
            </div>
          </div>
        </Form>
      </Drawer>

      <Drawer
        title='Factura furnizor'
        width={960}
        open={supplierInvoiceDrawerOpen}
        onClose={() => setSupplierInvoiceDrawerOpen(false)}
        extra={
          <Button type='primary' onClick={handleSupplierInvoiceSubmit}>
            Salveaza
          </Button>
        }
      >
        <Form form={supplierInvoiceForm} layout='vertical'>
          <Form.Item name='id' hidden>
            <Input />
          </Form.Item>
          <Space style={{ width: '100%' }} size={16} wrap>
            <Form.Item
              label='Numar factura'
              name='invoiceNumber'
              rules={[{ required: true, message: 'Introdu numarul facturii.' }]}
            >
              <Input className='erp-inline-field' />
            </Form.Item>
            <Form.Item
              label='Furnizor'
              name='supplierId'
              rules={[{ required: true, message: 'Selecteaza furnizorul.' }]}
            >
              <Select
                className='erp-inline-field-wide'
                showSearch
                optionFilterProp='label'
                options={supplierOptions.map((item) => ({
                  value: item.id,
                  label: `${item.name} (${item.cui || '-'})`,
                }))}
              />
            </Form.Item>
            <Form.Item
              label='Data emitere'
              name='issueDate'
              rules={[{ required: true, message: 'Selecteaza data emiterii.' }]}
            >
              <DatePicker format='DD.MM.YYYY' />
            </Form.Item>
            <Form.Item
              label='Data scadenta'
              name='dueDate'
              rules={[{ required: true, message: 'Selecteaza scadenta.' }]}
            >
              <DatePicker format='DD.MM.YYYY' />
            </Form.Item>
            <Form.Item
              label='Status'
              name='status'
              rules={[{ required: true, message: 'Selecteaza statusul.' }]}
            >
              <Select
                options={[
                  { value: 'Draft', label: 'Draft' },
                  { value: 'Emisa', label: 'Emisa' },
                  { value: 'Platita', label: 'Platita' },
                ]}
              />
            </Form.Item>
          </Space>

          <Form.List name='lines'>
            {(fields, { add, remove }) => (
              <>
                <div className='erp-lines-header'>
                  <h3>Linii factura</h3>
                  <Button onClick={() => add({ quantity: 1, unitPrice: 0, vatPercentage: 19 })}>Adauga linie</Button>
                </div>
                {fields.map((field, index) => (
                  <Space key={field.key} align='start' style={{ display: 'flex', marginBottom: 8 }} wrap>
                    <Form.Item
                      {...field}
                      label='Produs'
                      name={[field.name, 'productId']}
                      rules={[{ required: true, message: 'Selecteaza produsul.' }]}
                    >
                      <Select
                        className='erp-inline-field-wide'
                        showSearch
                        optionFilterProp='label'
                        options={productOptions.map((item) => ({
                          value: item.id,
                          label: `${item.sku || '-'} - ${item.name}`,
                        }))}
                        onChange={(value) => setSupplierInvoiceLineDefaults(index, value)}
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      label='Cantitate'
                      name={[field.name, 'quantity']}
                      rules={[{ required: true, message: 'Cantitate.' }]}
                    >
                      <InputNumber min={1} precision={0} />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      label='Pret unitar'
                      name={[field.name, 'unitPrice']}
                      rules={[{ required: true, message: 'Pret unitar.' }]}
                    >
                      <InputNumber min={0} precision={4} />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      label='TVA %'
                      name={[field.name, 'vatPercentage']}
                      rules={[{ required: true, message: 'TVA.' }]}
                    >
                      <InputNumber min={0} precision={2} />
                    </Form.Item>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                      style={{ marginTop: 30 }}
                    />
                  </Space>
                ))}
              </>
            )}
          </Form.List>

          <div className='erp-summary'>
            <div className='erp-summary-card'>
              <strong>Total net:</strong>
              <div>{supplierTotals.totalNet.toFixed(4)}</div>
            </div>
            <div className='erp-summary-card'>
              <strong>Total TVA:</strong>
              <div>{supplierTotals.totalVat.toFixed(4)}</div>
            </div>
            <div className='erp-summary-card'>
              <strong>Total brut:</strong>
              <div>{supplierTotals.totalGross.toFixed(4)}</div>
            </div>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

const buildPagination = (
  pagination: { current: number; pageSize: number; total: number },
  setPagination: React.Dispatch<React.SetStateAction<{ current: number; pageSize: number; total: number }>>,
) => ({
  current: pagination.current,
  pageSize: pagination.pageSize,
  total: pagination.total,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50', '100'],
  onChange: (page: number, pageSize: number) => {
    setPagination((prev) => ({ ...prev, current: page, pageSize }));
  },
  onShowSizeChange: (_page: number, pageSize: number) => {
    setPagination((prev) => ({ ...prev, current: 1, pageSize }));
  },
});

const computeInvoiceTotals = (lines: any[]) => {
  return (lines || []).reduce(
    (acc, line) => {
      const quantity = Number(line?.quantity || 0);
      const unitPrice = Number(line?.unitPrice || 0);
      const vat = Number(line?.vatPercentage || 0);
      const lineNet = quantity * unitPrice;
      const lineVat = (lineNet * vat) / 100;

      acc.totalNet += lineNet;
      acc.totalVat += lineVat;
      acc.totalGross += lineNet + lineVat;
      return acc;
    },
    { totalNet: 0, totalVat: 0, totalGross: 0 },
  );
};

export default ERP;
